import * as React from 'react'
import { ReactElement, useEffect } from 'react'
import { ToastIntent } from '@fluentui/react-components'
import { useQueue, useQueueIds, useQueueJob } from '../../hooks/useQueue'
import useQueueCancel from '../../hooks/queue/useQueueCancel'
import useAppToast from '../../hooks/useAppToast'
import { AnyQueueData, QueueStatus, terminalQueueStatuses } from '../../hooks/queue/queueTypes'
import QueueToastContent from './QueueToastContent'

/** How long a job may sit in the queue before we warn the user that it is taking longer than expected. */
export const QUEUE_DELAY_TIMEOUT = 30000

const QueueStatusIntent = new Map<QueueStatus, ToastIntent>([
    [QueueStatus.Duplicate, 'error'],
    [QueueStatus.Queued, 'info'],
    [QueueStatus.Delayed, 'warning'],
    [QueueStatus.Cancelled, 'success'],
    [QueueStatus.Finished, 'success'],
    [QueueStatus.Failed, 'error'],
])

/**
 * Renders a toast for every job on the queue.
 *
 * The queue is the source of truth: this component subscribes to the set of job ids and mounts one QueueJobToast per
 * job, which in turn subscribes to that single job. A job that changes therefore only re-renders its own toast.
 */
export default function QueueToaster(): ReactElement {
    const ids = useQueueIds()

    return (
        <>
            {ids.map(id => (
                <QueueJobToast key={id} id={id} />
            ))}
        </>
    )
}

/**
 * Drives a single job: its toast, the delay timer, the status callbacks and the eventual removal from the queue.
 * Renders nothing itself — the toast lives in the toaster, which is what lets a closing toast outlive its job.
 */
function QueueJobToast({ id }: { id: string }): ReactElement {
    const queue = useQueue()
    const cancel = useQueueCancel()
    const toast = useAppToast()
    const job = useQueueJob<AnyQueueData>(id)
    const status = job?.status?.current

    // Show the toast, or recolour it as the job progresses. The content updates itself, see QueueToastContent.
    useEffect(() => {
        if (status) {
            toast.upsert(id, <QueueToastContent id={id} initialJob={job} />, { intent: QueueStatusIntent.get(status) || 'info' })
        }
    }, [status])

    // Warn the user when the server does not pick the job up within a reasonable amount of time. Clearing the timer is
    // the effect's own cleanup, so it cannot outlive the status it belongs to.
    useEffect(() => {
        if (status !== QueueStatus.Queued) {
            return undefined
        }

        const timer = setTimeout(() => queue.transition(id, QueueStatus.Delayed), QUEUE_DELAY_TIMEOUT)

        return () => clearTimeout(timer)
    }, [status])

    useEffect(() => {
        if (status === QueueStatus.Cancelled) {
            cancel(id, true, job)
        }
    }, [status])

    // Run the callbacks that the dispatcher registered on the job.
    useEffect(() => {
        if (status) {
            job.context?.callbacks?.[status]?.(job)
        }
    }, [job])

    // Take the job off the queue once it can no longer change. This runs after the effect that shows the toast, so the
    // final state is on screen before this component unmounts.
    useEffect(() => {
        if (terminalQueueStatuses.includes(status)) {
            queue.remove(id)
        }
    }, [job])

    return null
}
